/**
 * Date-range arithmetic on ISO ranges — the prototype's reducer helpers,
 * moved out of React so the Redux filters slice can use them.
 */
import { addDays, orderRange, startOfDay } from '@deepdive-ui';

import type { DateRangeValue } from '@/data-sources/contract/filters';

import { fromIsoDate, toIsoDate, type IsoDate } from './dates';

const DAY = 86_400_000;

/** The prototype's default window: the last 70 days, ending today. */
export const DEFAULT_RANGE_DAYS = 70;

export function defaultRange(today: IsoDate): DateRangeValue {
  const end = fromIsoDate(today);
  return { start: toIsoDate(addDays(end, -(DEFAULT_RANGE_DAYS - 1))), end: today };
}

/** Order the ends and drop any time of day. */
export function normaliseRange(range: DateRangeValue): DateRangeValue {
  const ordered = orderRange({
    start: range.start ? fromIsoDate(range.start) : null,
    end: range.end ? fromIsoDate(range.end) : null,
  });
  return {
    start: ordered.start ? toIsoDate(startOfDay(ordered.start)) : null,
    end: ordered.end ? toIsoDate(startOfDay(ordered.end)) : null,
  };
}

/** The same number of days immediately before `range`; null for an open range. */
export function previousPeriod(range: DateRangeValue): DateRangeValue | null {
  const { start, end } = normaliseRange(range);
  if (!start || !end) return null;
  const from = fromIsoDate(start);
  const days = Math.round((fromIsoDate(end).getTime() - from.getTime()) / DAY) + 1;
  return { start: toIsoDate(addDays(from, -days)), end: toIsoDate(addDays(from, -1)) };
}

export function toPickerRange(range: DateRangeValue): { start: Date | null; end: Date | null } {
  return {
    start: range.start ? fromIsoDate(range.start) : null,
    end: range.end ? fromIsoDate(range.end) : null,
  };
}

export function fromPickerRange(range: { start: Date | null; end: Date | null }): DateRangeValue {
  return {
    start: range.start ? toIsoDate(range.start) : null,
    end: range.end ? toIsoDate(range.end) : null,
  };
}
